import { StyleSheet } from '@react-pdf/renderer';
import { InvoicePdfModel } from './viewModel';

export const SIGNATURE_MAX_WIDTH = 240;
export const SIGNATURE_MAX_HEIGHT = 96;

export const invoicePdfContentWidth = (model: InvoicePdfModel) => {
    const pageWidth = model.pdfPageSize === 'LETTER' ? 612 : 595.28;
    const padding = model.theme === 'minimal' ? 36 : 42;
    return pageWidth - padding * 2;
};

const tint = (hex: string, alpha: number) => {
    const raw = hex.replace('#', '').trim();
    const full = raw.length === 3 ? raw.split('').map((char) => char + char).join('') : raw;
    if (!/^[0-9a-fA-F]{6}$/.test(full)) return `rgba(15, 23, 42, ${alpha})`;
    const red = parseInt(full.slice(0, 2), 16);
    const green = parseInt(full.slice(2, 4), 16);
    const blue = parseInt(full.slice(4, 6), 16);
    return `rgba(${red}, ${green}, ${blue}, ${alpha})`;
};

export const createInvoicePdfStyles = (model: InvoicePdfModel) => StyleSheet.create({
    page: {
        color: '#0f172a',
        paddingTop: model.theme === 'minimal' ? 30 : 36,
        paddingHorizontal: model.theme === 'minimal' ? 36 : 42,
        paddingBottom: model.theme === 'minimal' ? 30 : 42,
        position: 'relative',
        fontFamily: model.fontFamily,
    },
    accent: {
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        height: 5,
        backgroundColor: model.accentColor,
    },
    main: {
        flexShrink: 0,
    },
    header: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'flex-start',
        marginTop: 12,
        marginBottom: 28,
    },
    brand: {
        flexShrink: 0,
        alignItems: 'flex-start',
        paddingRight: 16,
    },
    heading: {
        width: '42%',
        marginLeft: 'auto',
        alignItems: 'stretch',
    },
    title: {
        fontSize: 22,
        letterSpacing: -0.4,
        fontFamily: model.boldFontFamily,
        textAlign: 'right',
    },
    number: {
        marginTop: 8,
        color: '#64748b',
        fontSize: 10,
        textAlign: 'right',
    },
    signatureStatus: {
        marginTop: 6,
        fontSize: 9,
        textAlign: 'right',
        fontFamily: model.boldFontFamily,
        color: model.signatureState === 'signed' ? '#135A2A' : '#64748b',
    },
    wordmark: {
        fontSize: 24,
        letterSpacing: -0.5,
        fontFamily: model.boldFontFamily,
    },
    seller: {
        marginTop: 10,
    },
    sellerName: {
        color: '#0f172a',
        fontSize: 10,
        lineHeight: 1.4,
        fontFamily: model.fontFamily,
    },
    sellerLine: {
        color: '#64748b',
        fontSize: 9,
        lineHeight: 1.4,
        fontFamily: model.fontFamily,
    },
    meta: {
        flexDirection: 'row',
        marginBottom: 28,
    },
    metaBillTo: {
        width: '46%',
        paddingRight: 12,
    },
    metaDate: {
        width: '27%',
        paddingRight: 12,
    },
    label: {
        color: '#94a3b8',
        fontSize: 8,
        letterSpacing: 0.8,
        textTransform: 'uppercase',
        marginBottom: 6,
        fontFamily: model.boldFontFamily,
    },
    value: {
        fontSize: 10,
        lineHeight: 1.45,
        color: '#334155',
        fontFamily: model.fontFamily,
    },
    buyerName: {
        color: '#0f172a',
        fontSize: 11,
        lineHeight: 1.45,
        fontFamily: model.fontFamily,
    },
    dateLabel: {
        color: '#94a3b8',
        fontSize: 8,
        letterSpacing: 0.8,
        textTransform: 'uppercase',
        marginBottom: 6,
        fontFamily: model.fontFamily,
    },
    dateValue: {
        color: '#0f172a',
        fontSize: 12,
        fontFamily: model.fontFamily,
    },
    table: {
        width: '100%',
    },
    tableHeader: {
        flexDirection: 'row',
        width: '100%',
        borderBottomWidth: 2,
        borderBottomColor: model.accentColor,
        paddingBottom: 6,
        marginBottom: 2,
    },
    tableHeaderCell: {
        minWidth: 0,
        paddingHorizontal: 3,
    },
    tableHeaderText: {
        fontSize: 7,
        letterSpacing: 0.2,
        textTransform: 'uppercase',
        color: '#64748b',
        fontFamily: model.boldFontFamily,
    },
    tableHeaderTextRight: {
        textAlign: 'right',
    },
    tableRow: {
        flexDirection: 'row',
        width: '100%',
        borderBottomWidth: 1,
        borderBottomColor: '#e2e8f0',
        paddingVertical: 5,
    },
    tableCell: {
        minWidth: 0,
        paddingHorizontal: 3,
    },
    tableCellFull: {
        width: '100%',
    },
    tableCellText: {
        fontSize: 8,
        color: '#0f172a',
    },
    tableCellTextRight: {
        textAlign: 'right',
    },
    totals: {
        marginTop: 10,
        marginLeft: 'auto',
        width: 210,
    },
    totalRow: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        paddingVertical: 4,
        fontSize: 10,
        color: '#334155',
    },
    grandTotal: {
        fontSize: 12,
        color: '#0f172a',
        fontFamily: model.boldFontFamily,
    },
    note: {
        marginTop: 24,
        paddingVertical: 12,
        paddingHorizontal: 14,
        borderRadius: 0,
        backgroundColor: tint(model.accentColor, 0.03),
    },
    signature: {
        marginTop: 28,
        flexShrink: 0,
        paddingVertical: 16,
        paddingHorizontal: 18,
        borderWidth: 1,
        borderColor: '#e2e8f0',
        borderRadius: 8,
        alignItems: 'flex-start',
    },
    signatureLabel: {
        fontSize: 8,
        letterSpacing: 1.2,
        textTransform: 'uppercase',
        color: '#64748b',
    },
    signatureAgreement: {
        marginTop: 6,
        fontSize: 11,
        color: '#0f172a',
        fontFamily: model.fontFamily,
    },
    signatureMark: {
        paddingTop: 14,
        paddingBottom: 0,
    },
    signatureImage: {
        maxWidth: SIGNATURE_MAX_WIDTH,
        maxHeight: SIGNATURE_MAX_HEIGHT,
    },
    signatureName: {
        fontSize: 18,
        fontFamily: model.italicFontFamily,
        color: '#0f172a',
    },
    signatureMetaRow: {
        flexDirection: 'row',
        alignItems: 'center',
    },
    signatureMeta: {
        fontSize: 9,
        color: '#64748b',
    },
    signatureMetaDivider: {
        width: 1,
        height: 12,
        marginHorizontal: 10,
        backgroundColor: '#e2e8f0',
    },
    noteTitle: {
        marginBottom: 4,
        fontSize: 10,
        color: '#0f172a',
        fontFamily: model.boldFontFamily,
    },
    footer: {
        position: 'absolute',
        bottom: model.theme === 'minimal' ? 18 : 24,
        left: model.theme === 'minimal' ? 36 : 42,
        right: model.theme === 'minimal' ? 36 : 42,
        paddingTop: 14,
        borderTopWidth: 1,
        borderTopColor: '#e2e8f0',
        color: '#64748b',
        fontSize: 10,
    },
    footerBlock: {
        marginTop: 10,
        alignItems: 'flex-start',
    },
    footerBlockCenter: {
        alignItems: 'center',
    },
    footerBlockRight: {
        alignItems: 'flex-end',
    },
    footerText: {
        width: '100%',
    },
    footerTextCenter: {
        textAlign: 'center',
    },
    footerTextRight: {
        textAlign: 'right',
    },
    markdownBold: {
        fontFamily: model.boldFontFamily,
    },
    markdownItalic: {
        fontFamily: model.italicFontFamily,
    },
    watermark: {
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        alignItems: 'center',
        justifyContent: 'center',
    },
    watermarkText: {
        fontSize: 86,
        letterSpacing: 10,
        color: 'rgba(232, 80, 91, 0.22)',
        fontFamily: model.boldFontFamily,
        transform: 'rotate(-35deg)',
    },
});
